import React, { useRef, useState, useMemo, useEffect, useCallback } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import useSupercluster from 'use-supercluster';
import { Row, Col } from 'react-bootstrap';
import { Maximize, Minimize } from 'lucide-react';
import { Text, Button } from 'v3/components';
import theme from 'v3/theme';
import { FullscreenButton, MapsContainer } from './styles';
import MapDetailCard from './MapDetailCard';
import CardSubtitleVehicleTower from '../CardSubtitleVehicleTower';

const DEFAULT_CENTER = [-14.235, -51.9253];
const DEFAULT_ZOOM = 4;
const LEAFLET_HEIGHT_OFFSET_PX = 14;

function createPinIcon(background, sizeIcon = 22, color = '#fff') {
  const isSvg = String(background).includes('.svg');
  const backgroundCss = isSvg
    ? `background-image: url(/images/${background}); background-size: 100% 100%;`
    : `background: ${background};`;

  return L.divIcon({
    className: '',
    html: `
      <div style="
        width: ${sizeIcon}px;
        height: ${sizeIcon}px;
        border-radius: 0% 50% 50% 50%;
        ${backgroundCss}
        transform: rotate(-135deg);
        transform-origin: top left;
        position: absolute;
        left: 50%;
        top: 50%;
        margin: 0;
      ">
        <div style="
          width: 14px;
          height: 14px;
          margin: 10px 0 0 10px;
          transform: rotate(135deg);
          border-radius: 50%;
          color: ${color};
        "></div>
      </div>
    `,
  });
}

function createClusterIcon(pointCount, totalPoints) {
  const iconSize = 10 + (pointCount / Math.max(totalPoints, 1)) * 20;
  return L.divIcon({
    className: '',
    html: `
      <div style="
        width: ${iconSize}px;
        height: ${iconSize}px;
        color: #fff;
        background: #1978c8;
        border-radius: 50%;
        padding: 10px;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 12px;
        font-weight: 700;
      ">${pointCount}</div>
    `,
  });
}

function MapEventsHandler({ setBounds, setZoom, setChecked, mapRef }) {
  const map = useMapEvents({
    click: () => setChecked(false),
    zoomend: () => {
      const mapBounds = map.getBounds();
      setZoom(map.getZoom());
      setBounds([mapBounds.getWest(), mapBounds.getSouth(), mapBounds.getEast(), mapBounds.getNorth()]);
    },
    moveend: () => {
      const mapBounds = map.getBounds();
      setBounds([mapBounds.getWest(), mapBounds.getSouth(), mapBounds.getEast(), mapBounds.getNorth()]);
    },
  });

  useEffect(() => {
    mapRef.current = map;
    const mapBounds = map.getBounds();
    setZoom(map.getZoom());
    setBounds([mapBounds.getWest(), mapBounds.getSouth(), mapBounds.getEast(), mapBounds.getNorth()]);

    return () => {
      if (mapRef.current === map) {
        mapRef.current = null;
      }
    };
  }, [map, mapRef, setBounds, setZoom]);

  return null;
}

function ResizeController({ trigger }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return undefined;

    map.invalidateSize(true);

    const timeout = setTimeout(() => {
      map.invalidateSize(true);
    }, 180);

    return () => clearTimeout(timeout);
  }, [map, trigger]);

  return null;
}

export default function MapClusterVehicleTower({
  vehiclesData,
  toggleMaps,
  setToggleMaps,
  loading,
  checked,
  setChecked,
  alignedMapHeight,
}) {
  const [detail, setDetail] = useState([]);
  const mapRef = useRef();
  const mapContainerRef = useRef(null);
  const [bounds, setBounds] = useState(null);
  const [zoom, setZoom] = useState(0);
  const [fullScreen, setFullScreen] = useState(false);
  const resolvedMapHeight =
    Number.isFinite(alignedMapHeight) && alignedMapHeight > 0
      ? `${Math.round(alignedMapHeight + LEAFLET_HEIGHT_OFFSET_PX)}px`
      : '64vh';
  const points = useMemo(() => {
    if (vehiclesData) {
      const pVehicles = vehiclesData
        ?.filter(v => {
          return v?.latitude && v?.longitude;
        })
        ?.map(vehicle => ({
          type: 'Feature',
          properties: {
            cluster: false,
            id: vehicle.vehicleId,
            category: vehicle.vehicleStatus,
          },
          geometry: {
            type: 'Point',
            coordinates: [
              parseFloat(vehicle?.longitude),
              parseFloat(vehicle?.latitude),
            ],
          },
        }));
      return [...pVehicles];
    } else {
      return [];
    }
  }, [vehiclesData]);
  const { clusters, supercluster } = useSupercluster({
    points,
    bounds,
    zoom,
    options: { radius: 75, minZoom: 0, maxZoom: 20, minPoints: 2 },
  });

  function retrieveVehicle(id) {
    const vehicle = vehiclesData.filter(v => v.vehicleId === id);
    setChecked(true);
    setDetail(vehicle[0]);
  }
  function stopMarkerClickPropagation(event) {
    if (event?.originalEvent) {
      L.DomEvent.stopPropagation(event.originalEvent);
    }
  }
  function handleChangeColor(category) {
    const colorMap = {
      inTravel: theme.colors.light_blue,
      emptyVehicle: theme.colors.success,
      inTravelNotTracked: theme.colors.yellow,
      emptyVehicleNotTracked: theme.colors.orange,
    };
    return colorMap[category] || '';
  }

  const toggleFullScreen = useCallback(() => {
    const element = mapContainerRef.current;
    if (!element) return;

    const fullscreenElement =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement;

    if (fullscreenElement) {
      const exit =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.mozCancelFullScreen ||
        document.msExitFullscreen;

      if (exit) exit.call(document);
      return;
    }

    const enter =
      element.requestFullscreen ||
      element.webkitRequestFullscreen ||
      element.mozRequestFullScreen ||
      element.msRequestFullscreen;

    if (enter) enter.call(element);
  }, []);

  useEffect(() => {
    function handleFullScreenChange() {
      const fullscreenElement =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;

      setFullScreen(fullscreenElement === mapContainerRef.current);
    }

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullScreenChange);
    document.addEventListener('mozfullscreenchange', handleFullScreenChange);
    document.addEventListener('MSFullscreenChange', handleFullScreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullScreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullScreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullScreenChange);
    };
  }, []);

  useEffect(() => {
    if (toggleMaps || loading) return undefined;

    let frameId = null;
    frameId = requestAnimationFrame(() => {
      const map = mapRef.current;
      if (!map || typeof map.invalidateSize !== 'function' || typeof map.getContainer !== 'function') {
        return;
      }

      const container = map.getContainer();
      if (!container || !container.isConnected) {
        return;
      }

      map.invalidateSize({ pan: false, debounceMoveend: true });
    });

    return () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [resolvedMapHeight, toggleMaps, loading]);

  return (
    <MapsContainer>
      <Row className="d-flex align-items-center">
        <Col>
          <Button onClick={() => setToggleMaps(!toggleMaps)}>
            <Text type="regular" weight={500} fontSize="0.75rem">
              {toggleMaps ? 'Ver mapa' : 'Minimizar'}
            </Text>
          </Button>
        </Col>
      </Row>
      <>
        {toggleMaps === false && loading === false ? (
          <>
            <Row>
              <Col>
                <div
                  ref={mapContainerRef}
                  style={{
                    height: fullScreen ? '100vh' : resolvedMapHeight,
                    minHeight: fullScreen ? '100vh' : '320px',
                    position: 'relative',
                    width: '100%',
                    background: '#fff',
                  }}
                >
                  <FullscreenButton
                    aria-label={fullScreen ? 'Sair da tela cheia' : 'Ver em tela cheia'}
                    title={fullScreen ? 'Sair da tela cheia' : 'Tela cheia'}
                    type="button"
                    onClick={toggleFullScreen}
                  >
                    {fullScreen ? <Minimize size={18} /> : <Maximize size={18} />}
                  </FullscreenButton>
                  <MapContainer
                    center={DEFAULT_CENTER}
                    zoom={DEFAULT_ZOOM}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom
                  >
                    <TileLayer
                      attribution='&copy; OpenStreetMap contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <MapEventsHandler
                      setBounds={setBounds}
                      setZoom={setZoom}
                      setChecked={setChecked}
                      mapRef={mapRef}
                    />
                    <ResizeController trigger={fullScreen} />
                    {clusters.map((cluster) => {
                      const [longitude, latitude] = cluster.geometry.coordinates;
                      const {
                        cluster: isCluster,
                        point_count: pointCount,
                        category,
                      } = cluster.properties;
                      if (isCluster) {
                        return (
                          <Marker
                            key={`cluster-${cluster.id}`}
                            position={[latitude, longitude]}
                            icon={createClusterIcon(pointCount, points?.length)}
                            eventHandlers={{
                              click: (event) => {
                                stopMarkerClickPropagation(event);
                                const expansionZoom = Math.min(
                                  supercluster.getClusterExpansionZoom(cluster.id),
                                  20,
                                );
                                if (mapRef.current && typeof mapRef.current.setView === 'function') {
                                  mapRef.current.setView([latitude, longitude], expansionZoom);
                                }
                              },
                            }}
                          >
                          </Marker>
                        );
                      }
                      return (
                        <Marker
                          key={cluster.properties.id}
                          position={[latitude, longitude]}
                          icon={createPinIcon(handleChangeColor(category), 22, '#fff')}
                          eventHandlers={{
                            click: (event) => {
                              stopMarkerClickPropagation(event);
                              retrieveVehicle(cluster.properties.id);
                            },
                          }}
                        />
                      );
                    })}
                  </MapContainer>
                  <Col>
                    {checked ? (
                      <>
                        <MapDetailCard
                          load={detail}
                          checked={checked}
                          onClose={() => setChecked(false)}
                        ></MapDetailCard>
                      </>
                    ) : null}
                  </Col>
                </div>
              </Col>
            </Row>
            <CardSubtitleVehicleTower />
          </>
        ) : (
          <></>
        )}
      </>
    </MapsContainer>
  );
}
